"use client";

import { useMemo } from "react";
import { z } from "zod";
import { useWebMcpTool } from "@/lib/webmcp/react";
import type { WebMcpAction } from "@/lib/webmcp/manifest";

type NativeField = {
  readonly name: string;
  readonly label: string;
  readonly maxLength?: number;
  readonly type: "email" | "text" | "select";
  readonly options?: readonly string[];
};

type WebMcpNativeFormProps = {
  readonly action: WebMcpAction;
  readonly instance: string;
  readonly selector: string;
  readonly fields?: readonly NativeField[];
};

function fieldSchema(field: NativeField) {
  if (field.type === "select") {
    const options = [...new Set(field.options ?? [])];
    return options.length > 0
      ? z.enum(options as [string, ...string[]]).describe(field.label)
      : z.never().describe(field.label);
  }
  const value = z.string().max(field.maxLength ?? 254).describe(field.label);
  return field.type === "email" ? value.email() : value;
}

/** Binds marked native forms; it only prepares visible non-sensitive controls. */
export function WebMcpNativeForm({ action, instance, selector, fields = [] }: WebMcpNativeFormProps) {
  const fieldNamesAreUnique = new Set(fields.map((field) => field.name)).size === fields.length;
  const schema = useMemo(() => {
    const shape: Record<string, z.ZodType> = {};
    for (const field of fields) shape[field.name] = fieldSchema(field);
    return z.object(shape).strict();
  }, [fields]);

  useWebMcpTool({
    action,
    instance,
    schema,
    enabled: fieldNamesAreUnique,
    execute: (input) => {
      const form = document.querySelector(selector);
      if (!(form instanceof HTMLFormElement) || form.getClientRects().length === 0) return { status: "unavailable" };

      const prepared: Array<{ field: NativeField; control: NativeControl; value: string }> = [];
      for (const field of fields) {
        const control = form.elements.namedItem(field.name);
        const value = input[field.name];
        if (typeof value !== "string" || !isExpectedControl(control, field) || !isWritableVisibleControl(control)) {
          return { status: "unavailable" };
        }
        if (
          (control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement)
          && control.maxLength >= 0
          && value.length > control.maxLength
        ) return { status: "unavailable" };
        if (control instanceof HTMLSelectElement && !isEnabledOption(control, value)) return { status: "unavailable" };
        prepared.push({ field, control, value });
      }

      for (const { control, value } of prepared) {
        control.value = value;
        control.dispatchEvent(new Event("input", { bubbles: true }));
        control.dispatchEvent(new Event("change", { bubbles: true }));
      }

      const focusTarget = prepared[0]?.control ?? form.querySelector<HTMLElement>('button[type="submit"]');
      if (focusTarget instanceof HTMLElement && isVisible(focusTarget) && !focusTarget.matches(":disabled")) focusTarget.focus();
      return { status: "review_required", uiState: { preparedFields: fields.map((field) => field.name) } };
    },
  });
  return null;
}

type NativeControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

function isExpectedControl(control: Element | RadioNodeList | null, field: NativeField): control is NativeControl {
  if (field.type === "email") return control instanceof HTMLInputElement && control.type === "email";
  if (field.type === "text") {
    return (control instanceof HTMLInputElement && control.type === "text") || control instanceof HTMLTextAreaElement;
  }
  return control instanceof HTMLSelectElement && !control.multiple;
}

function isWritableVisibleControl(control: NativeControl) {
  if (control.matches(":disabled") || !isVisible(control)) return false;
  if (control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement) return !control.readOnly;
  return true;
}

function isEnabledOption(control: HTMLSelectElement, value: string) {
  return Array.from(control.options).some((option) => (
    option.value === value
    && !option.disabled
    && !(option.parentElement instanceof HTMLOptGroupElement && option.parentElement.disabled)
  ));
}

function isVisible(element: HTMLElement) {
  if (element.closest('[hidden], [aria-hidden="true"]')) return false;
  const styles = window.getComputedStyle(element);
  return element.getClientRects().length > 0 && styles.display !== "none" && styles.visibility !== "hidden" && styles.visibility !== "collapse";
}
